import api from 'config/axiosConfig';
import { apiRoutes } from "util/apiRoutes";

// Types for API responses
interface ApiResponse<T = any> {
    data: T;
    message?: string;
}

// Privacy levels for team data
export type PrivacyLevel = 'public' | 'follow' | 'teams' | 'besteas' | 'me' | 'admin' | 'anonymous';
export type TeamMessageReceiveOption = "public" | "followers" | "no_one";
export type TeamInboxMode = "all_members" | "moderators_only";

// Types for different setting categories
export interface TeamPrivacySettings {
    is_private?: boolean;
    content_default_audience?: PrivacyLevel;
    message_receive?: TeamMessageReceiveOption[];
    message_inbox_mode?: TeamInboxMode;
}

export interface TeamPreferenceSettings {
    // Add team preference settings as needed
}

export interface AboutValue<T = any> {
    value: T;
    privacy: PrivacyLevel;
}

export interface TeamAboutSettings {
    bio_brief: AboutValue<string>;
    bio_links: AboutValue<Array<{ type: 'custom' | 'instagram' | 'x' | 'facebook' | 'youtube' | 'tiktok' | 'reddit', label: string; url: string }>>;
    bio_issue: AboutValue<number | null>;
}

export async function updateTeamIsPrivate(teamId: number, isPrivate: boolean): Promise<ApiResponse> {
    const response = await api.patch(apiRoutes.team.private(teamId), {
        is_private: isPrivate
    });
    return response;
}

export async function updateTeamAllowContentPromotion(teamId: number, enabled: boolean): Promise<ApiResponse> {
    const response = await api.patch(apiRoutes.team.allowContentPromotion(teamId), {
        enabled,
    });
    return response;
}

// Helper function for privacy settings
export async function updateTeamPrivacySetting<K extends keyof TeamPrivacySettings>(
    teamId: number,
    category: string,
    key: string,
    value: TeamPrivacySettings[K]
): Promise<ApiResponse> {
    try {
        const response = await api.patch(apiRoutes.team.privacy(teamId), {
            category,
            key,
            value
        });
        return response;
    } catch (error) {
        console.error('Error updating team privacy setting:', error);
        throw error;
    }
}

// Helper function for preference settings
export async function updateTeamPreferenceSetting<K extends keyof TeamPreferenceSettings>(
    teamId: number,
    category: string,
    key: string,
    value: TeamPreferenceSettings[K]
): Promise<ApiResponse> {
    try {
        const response = await api.patch(apiRoutes.team.preferences(teamId), {
            category,
            key,
            value
        });
        return response;
    } catch (error) {
        console.error('Error updating team preference setting:', error);
        throw error;
    }
}

// Helper function for about settings
export async function updateTeamAboutSetting<K extends keyof TeamAboutSettings>(
    teamId: number,
    category: string,
    key: string,
    value: TeamAboutSettings[K] extends AboutValue<infer T> ? T : never,
    privacy: PrivacyLevel = 'public'
): Promise<ApiResponse> {
    try {
        const response = await api.patch(apiRoutes.team.about(teamId), {
            category,
            key,
            value: {
                value,
                privacy
            }
        });
        return response;
    } catch (error) {
        console.error('Error updating team about setting:', error);
        throw error;
    }
}

// Specific helper functions for common operations
export async function createTeam(
    teamname: string,
    display_name: string,
    is_private: boolean,
    bio_brief?: string
): Promise<ApiResponse> {
    try {
        const response = await api.post(apiRoutes.team.root, {
            teamname,
            display_name,
            is_private,
            ...(bio_brief && { bio_brief })
        });
        return response;
    } catch (error) {
        console.error('Error creating team:', error);
        throw error;
    }
}

export async function updateTeamPrivateStatus(teamId: number, isPrivate: boolean): Promise<ApiResponse> {
    return updateTeamIsPrivate(teamId, isPrivate);
}

export async function updateTeamDefaultAudience(
    teamId: number,
    audience: PrivacyLevel
): Promise<ApiResponse> {
    return updateTeamPrivacySetting(teamId, 'content', 'default_audience', audience);
}

export async function updateTeamMessagePrivacy(
    teamId: number,
    recipients: TeamPrivacySettings['message_receive']
): Promise<ApiResponse> {
    return updateTeamPrivacySetting(teamId, 'message', 'receive', recipients);
}

export async function updateTeamMessageInboxMode(
    teamId: number,
    mode: TeamInboxMode
): Promise<ApiResponse> {
    return updateTeamPrivacySetting(teamId, "message", "inbox_mode", mode);
}

export async function updateTeamBio(
    teamId: number,
    bio: string,
    privacy: PrivacyLevel = 'public'
): Promise<ApiResponse> {
    return updateTeamAboutSetting(teamId, 'bio', 'brief', bio, privacy);
}

export async function updateTeamLinks(
    teamId: number,
    links: Array<{ type: 'custom' | 'instagram' | 'x' | 'facebook' | 'youtube' | 'tiktok' | 'reddit', label: string; url: string }>,
    privacy: PrivacyLevel = 'public'
): Promise<ApiResponse> {
    return updateTeamAboutSetting(teamId, 'bio', 'links', links, privacy);
}

export async function updateTeamTopIssue(
    teamId: number,
    issue: number | null,
    privacy: PrivacyLevel = 'public'
): Promise<ApiResponse> {
    return updateTeamAboutSetting(teamId, 'bio', 'issue', issue, privacy);
} 